import { useState } from 'react';
import { CalendarPlus, Bell } from 'lucide-react';
import { seedNextMonthFlexPlans, sendFlexReminders } from '../../services/flexPlans';
import { defaultSeedMonth } from '../../utils/flexPlan';

/**
 * Admin "Prepare next-month flex plans" batch op — seeds draft plans for active
 * flex_monthly enrollments that don't yet have one for the target month (default: next
 * calendar month). MVP replacement for a daily seeding cron; run it monthly before the
 * 15th so parents can review/adjust before the lock. Self-contained (own mutation +
 * confirm) to keep its footprint in the finance page minimal.
 */
export default function FlexSeedButton({ className = '' }: { className?: string }) {
  const [running, setRunning] = useState(false);

  async function run() {
    const target = defaultSeedMonth();
    const ok = window.confirm(
      `Prepare flex plans for ${target.slice(0, 7)}?\n\n` +
        'Seeds a draft plan for every active flex enrollment that doesn\'t already have ' +
        'one for that month (from the prior month\'s pattern or the enrollment\'s default ' +
        'weekdays). Existing plans are left untouched. Run this before the 15th so parents ' +
        'can review before the lock.',
    );
    if (!ok) return;
    setRunning(true);
    try {
      const res = await seedNextMonthFlexPlans();
      window.alert(
        `Flex plans for ${res.plan_month.slice(0, 7)}: seeded ${res.seeded} of ` +
          `${res.eligible} eligible enrollment(s).`,
      );
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to seed flex plans');
    } finally {
      setRunning(false);
    }
  }

  const [reminding, setReminding] = useState(false);
  async function remind() {
    const target = defaultSeedMonth();
    const ok = window.confirm(
      `Send flex plan reminders for ${target.slice(0, 7)}?\n\n` +
        'Emails every active flex parent to review and confirm next month\'s days before the ' +
        'lock on the 15th. Send it before the 15th.',
    );
    if (!ok) return;
    setReminding(true);
    try {
      const res = await sendFlexReminders();
      window.alert(`Flex reminders for ${res.plan_month.slice(0, 7)}: sent ${res.sent} of ${res.eligible} enrollment(s).`);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to send reminders');
    } finally {
      setReminding(false);
    }
  }

  const btn = 'hidden sm:inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50';

  return (
    <>
      <button
        type="button"
        onClick={() => void run()}
        disabled={running}
        className={className || btn}
        title="Seed next month's flex plans (draft) for active flex enrollments"
      >
        <CalendarPlus className="h-4 w-4" />
        {running ? 'Preparing…' : 'Prepare flex plans'}
      </button>
      <button
        type="button"
        onClick={() => void remind()}
        disabled={reminding}
        className={btn}
        title="Email flex parents to confirm next month's plan before the lock"
      >
        <Bell className="h-4 w-4" />
        {reminding ? 'Sending…' : 'Flex reminders'}
      </button>
    </>
  );
}
